"use strict";
/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2022 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Cart = void 0;
const EventEmitterFactory_1 = require("../events/EventEmitterFactory");
const CartData_1 = require("./CartData");
const CartRequestHandler_1 = require("./requestHandlers/CartRequestHandler");
class Cart {
    constructor(shopId, cartData, isBroadcastAllowed = false) {
        this.shopId = shopId;
        this.cartData = cartData;
        this.eventEmitter = EventEmitterFactory_1.EventEmitterFactory.getEmitter("CartDidChangeEvent", isBroadcastAllowed);
        this.eventEmitter.on((payload) => {
            if (payload === this.cartData) {
                return;
            }
            this.cartData = payload;
        });
        this.addItem = this.addItem.bind(this);
        this.removeItem = this.removeItem.bind(this);
        this.changeItemQuantity = this.changeItemQuantity.bind(this);
        this.configure = this.configure.bind(this);
        this.addVoucher = this.addVoucher.bind(this);
        this.removeVoucher = this.removeVoucher.bind(this);
        this.put = this.put.bind(this);
    }
    get amountGross() {
        return this.cartData.value.gross;
    }
    get amountNet() {
        return this.cartData.value.net;
    }
    get cartId() {
        return this.cartData.cartId;
    }
    get countryCode() {
        return this.cartData.countryCode;
    }
    get currencyCode() {
        return this.cartData.currencyCode;
    }
    get emitter() {
        return this.eventEmitter;
    }
    get tabInfo() {
        return this.emitter.tabInfo;
    }
    get isCompleted() {
        return this.cartData.isCompleted;
    }
    get isCorporateCustomer() {
        return this.cartData.isCorporateCustomer;
    }
    get isNet() {
        return this.cartData.isNet;
    }
    get isUsingPoints() {
        return this.cartData.isUsingPoints;
    }
    get isValid() {
        return this.cartData.isValid;
    }
    get logisticsTypeId() {
        return this.cartData.logisticsTypeId;
    }
    get messages() {
        return this.cartData.messages;
    }
    get paymentInformation() {
        return this.cartData.paymentInformation;
    }
    get positions() {
        return this.cartData.positions;
    }
    get promotion() {
        return this.cartData.promotion;
    }
    get rebates() {
        return this.cartData.rebates;
    }
    get shippingInformation() {
        return this.cartData.shippingInformation;
    }
    get taxAmounts() {
        return this.cartData.value.tax;
    }
    get valuePayable() {
        return this.cartData.value.payable;
    }
    get valueRebate() {
        return this.cartData.value.rebate;
    }
    get valueSubtotal() {
        return this.cartData.value.subtotal;
    }
    get vatNumber() {
        return this.cartData.vatNumber;
    }
    get visitorId() {
        return this.cartData.visitorId;
    }
    /**
     * Adds an item to the cart
     * @param {number} itemId
     * @param {number} quantity
     * @returns {Promise<void>}
     * @throws {CartError | ApiError}
     */
    addItem(itemId, quantity = 1) {
        return __awaiter(this, void 0, void 0, function* () {
            const newCartData = yield CartRequestHandler_1.CartRequestHandler.addItem(this.shopId, this.cartId, itemId, quantity);
            this.cartData = new CartData_1.CartData(newCartData);
            this.eventEmitter.emit(this.cartData);
        });
    }
    /**
     * Adds a voucher to the cart
     * @param {string} voucherCode
     * @returns {Promise<void>}
     * @throws {CartError | ApiError}
     */
    addVoucher(voucherCode) {
        return __awaiter(this, void 0, void 0, function* () {
            const newCartData = yield CartRequestHandler_1.CartRequestHandler.addVoucher(this.shopId, this.cartId, voucherCode);
            this.cartData = new CartData_1.CartData(newCartData);
            this.eventEmitter.emit(this.cartData);
        });
    }
    /**
     * Updates the quantity of the item
     * @param {number} itemId
     * @param {number} quantity
     * @returns {Promise<void>}
     * @throws {CartError | ApiError}
     */
    changeItemQuantity(itemId, quantity) {
        return __awaiter(this, void 0, void 0, function* () {
            const newCartData = yield CartRequestHandler_1.CartRequestHandler.addItem(this.shopId, this.cartId, itemId, quantity);
            this.cartData = new CartData_1.CartData(newCartData);
            this.eventEmitter.emit(this.cartData);
        });
    }
    /**
     * Configures parameters of the cart
     * @param {ICartRequestHandlerPutCartOptions} config
     * @returns {Promise<void>}
     * @throws {CartError | ApiError}
     */
    configure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            const newCartData = yield CartRequestHandler_1.CartRequestHandler.putParams(this.shopId, this.cartId, config);
            this.cartData = new CartData_1.CartData(newCartData);
            this.eventEmitter.emit(this.cartData);
        });
    }
    put(cartParams) {
        return __awaiter(this, void 0, void 0, function* () {
            const newCartData = yield CartRequestHandler_1.CartRequestHandler.putParams(this.shopId, this.cartId, cartParams);
            this.cartData = new CartData_1.CartData(newCartData);
            this.eventEmitter.emit(this.cartData);
        });
    }
    /**
     * Removes an item from the cart
     * @param {number} itemId
     * @returns {Promise<void>}
     * @throws {CartError | ApiError}
     */
    removeItem(itemId) {
        return __awaiter(this, void 0, void 0, function* () {
            const newCartData = yield CartRequestHandler_1.CartRequestHandler.removeItem(this.shopId, this.cartId, itemId);
            this.cartData = new CartData_1.CartData(newCartData);
            this.eventEmitter.emit(this.cartData);
        });
    }
    /**
     * Removes a voucher from the cart
     * @param {string} voucherCode
     * @returns {Promise<void>}
     * @throws {CartError | ApiError}
     */
    removeVoucher(voucherCode) {
        return __awaiter(this, void 0, void 0, function* () {
            const newCartData = yield CartRequestHandler_1.CartRequestHandler.removeVoucher(this.shopId, this.cartId, voucherCode);
            this.cartData = new CartData_1.CartData(newCartData);
            this.eventEmitter.emit(this.cartData);
        });
    }
    setCartData(cartData) {
        this.cartData = cartData;
    }
}
exports.Cart = Cart;
